import React, { useCallback, useRef, useEffect, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import CombineTypeModal from '../modals/combine/combine-type-modal';
import CombineVariantModal from '../modals/combine/combine-variants-modal';
import CombineAliasModal from '../modals/combine/combine-alias-modal';
import ConfirmActionModal from '../modals/combine/confirm-action-modal';
import { useProductContext } from '../../products/product-context';
import { combineProducts, combineVariants } from '../../../../store/products/products.api';
import { combineReducer, initialState } from '../modals/combine/combine-button-reducer';

import messages from '../../messages';
import style from '../../../../components/grid-header/reusable-grid-button.module.less';
import * as constants from '../constants/combine-products';
import { getUserFriendlyMessagesForErrorResponse } from '../../../../errors/user-friendly-error-message-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

const CombineButton = () => {
  const { selectedProductIds, setSelectedProductIds, refreshGridData } = useProductContext();
  const [state, dispatchLocal] = useReducer(combineReducer, initialState);
  const openTypeModal = useCallback(
    () =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.isTypeModalOpen,
        payload: true,
      }),
    [],
  );
  const closeTypeModal = useCallback(
    () =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.isTypeModalOpen,
        payload: false,
      }),
    [],
  );
  const openCombineAliasModal = useCallback(
    () =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.isCombineAliasModalOpen,
        payload: true,
      }),
    [],
  );
  const closeCombineAliasModal = useCallback(
    () =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.isCombineAliasModalOpen,
        payload: false,
      }),
    [],
  );
  const openCombineVariantModal = useCallback(
    () =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.isCombineVariantModalOpen,
        payload: true,
      }),
    [],
  );
  const closeCombineVariantModal = useCallback(
    () =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.isCombineVariantModalOpen,
        payload: false,
      }),
    [],
  );
  const closeConfirmActionModal = useCallback(
    () =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.isConfirmActionModalOpen,
        payload: false,
      }),
    [],
  );
  const bulkChangeHandler = useCallback((value) => dispatchLocal({ type: 'setBulk', payload: value }), []);

  const { combineType, combineData, isTypeModalOpen, isCombineAliasModalOpen, isCombineVariantModalOpen, isConfirmActionModalOpen } = state;

  const cancelablePromiseRef = useRef(null);

  useEffect(() => cancelablePromiseRef.current?.cancel, []);

  const makeApiCall = useCallback(() => {
    if (combineType === constants.IS_ALIAS) {
      const { survivorProductId } = combineData;
      const childProductIds = selectedProductIds.filter((id) => id !== survivorProductId);
      cancelablePromiseRef.current = combineProducts(survivorProductId, childProductIds);
      cancelablePromiseRef.current.promise.then(() => {
        closeConfirmActionModal();
        setSelectedProductIds([]);
        refreshGridData();
        cancelablePromiseRef.current = null;
      });
    }
    if (combineType === constants.IS_VARIANT) {
      const { parentName, parentSKU } = combineData;
      cancelablePromiseRef.current = combineVariants({ name: parentName, sku: parentSKU, parentProductId: null }, selectedProductIds);
      cancelablePromiseRef.current.promise
        .then(() => {
          closeConfirmActionModal();
          setSelectedProductIds([]);
          refreshGridData();
          cancelablePromiseRef.current = null;
        })
        .catch((resp) => {
          const responseData = resp.response?.data;
          const { errors } = getUserFriendlyMessagesForErrorResponse(responseData);
          const combineError = errors[0];
          const { messageDescriptor, values } = combineError;
          const errorPayload = { ...messageDescriptor, values };
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            title: <FormattedMessage {...messages.combineProductErrorTitle} />,
            content: <FormattedMessage {...errorPayload} />,
          });
          cancelablePromiseRef.current = null;
        });
    }
  }, [combineType, combineData, selectedProductIds, closeConfirmActionModal, setSelectedProductIds, refreshGridData]);

  const onCancel = useCallback(() => {
    bulkChangeHandler({
      combineType: constants.IS_EMPTY,
      combineData: {},
      [constants.isConfirmActionModalOpen]: false,
    });
  }, [bulkChangeHandler]);

  const alertActionUndoableModal = useCallback(
    (data) => {
      const propForModalNameToClose =
        combineType === constants.IS_ALIAS ? constants.isCombineAliasModalOpen : constants.isCombineVariantModalOpen;
      bulkChangeHandler({
        combineData: data,
        [propForModalNameToClose]: false,
        [constants.isConfirmActionModalOpen]: true,
      });
    },
    [combineType, bulkChangeHandler],
  );

  const onConfirmType = useCallback(
    (combineTypeChoosen) => {
      bulkChangeHandler({
        combineType: combineTypeChoosen,
        [constants.isTypeModalOpen]: false,
      });
      if (combineTypeChoosen === constants.IS_ALIAS) {
        openCombineAliasModal();
      } else if (combineTypeChoosen === constants.IS_VARIANT) {
        openCombineVariantModal();
      }
    },
    [bulkChangeHandler, openCombineAliasModal, openCombineVariantModal],
  );

  return (
    <>
      <Button className={style['reusable-button']} disabled={selectedProductIds.length < 2} onClick={openTypeModal} theme='tertiary'>
        <FormattedMessage {...messages.combineButton} />
      </Button>
      {isTypeModalOpen && (
        <CombineTypeModal onClose={closeTypeModal} onConfirmType={onConfirmType} selectedProductIds={selectedProductIds} />
      )}
      {isCombineAliasModalOpen && (
        <CombineAliasModal onClose={closeCombineAliasModal} onConfirm={alertActionUndoableModal} selectedProductIds={selectedProductIds} />
      )}
      {isCombineVariantModalOpen && (
        <CombineVariantModal
          onClose={closeCombineVariantModal}
          onConfirm={alertActionUndoableModal}
          selectedProductIds={selectedProductIds}
        />
      )}
      {isConfirmActionModalOpen && (
        <ConfirmActionModal combineType={combineType} onCancel={onCancel} onConfirm={makeApiCall} selectedProductIds={selectedProductIds} />
      )}
    </>
  );
};

export default CombineButton;
